import { EnforcementMode, StackInIssueCheckResponse, YardIssueType } from '@yard/app/api'

export interface StackInWarningPresentation {
  key: string
  fallback: string
  severity: 'warning' | 'error'
}

export interface ImdgStackInWarningHandoffPresentation {
  messageKey: string
  messageFallback: string
  issuesUrl?: string
}

type WarningPresentationCopy = Omit<StackInWarningPresentation, 'severity'>

const genericYardIssueWarning: WarningPresentationCopy = {
  key: 'theSelectedPositionHasYardIssueWarning',
  fallback: 'The selected position may create a Yard Issue.',
}

const genericYardIssueBlock: WarningPresentationCopy = {
  key: 'theSelectedPositionHasYardIssueBlock',
  fallback: 'The selected position is blocked by a Yard Issue rule. Choose another position or adjust rules.',
}

const warningPresentations: Record<YardIssueType, WarningPresentationCopy> = {
  [YardIssueType.ContainerInBlockedSlot]: {
    key: 'theSelectedSlotIsBlocked',
    fallback: 'The selected slot is blocked',
  },
  [YardIssueType.DuplicatePosition]: {
    key: 'theSelectedPositionIsAlreadyOccupied',
    fallback: 'The selected position is already occupied',
  },
  [YardIssueType.StackHeightExceeded]: {
    key: 'theSelectedPositionExceedsStackHeight',
    fallback:
      'The selected position cannot be stacked on since it would exceed the allowed maximum stack height.',
  },
  [YardIssueType.ReeferContainerInUnpoweredSlot]: {
    key: 'theSelectedSlotHasNoReeferPlug',
    fallback: 'The selected slot is not in a reefer plug area — this container needs to be plugged in.',
  },
  [YardIssueType.NonReeferContainerInReeferSlot]: {
    key: 'theSelectedSlotIsAReeferPlugArea',
    fallback:
      'The selected slot is in a reefer plug area — it should be reserved for containers that need to be plugged in.',
  },
  [YardIssueType.FlyingContainer]: {
    key: 'theSelectedPositionHasNoContainerBelow',
    fallback: 'The selected position has no container directly below',
  },
  [YardIssueType.ImdgSegregationViolation]: {
    key: 'theSelectedPositionHasImdgSegregationWarning',
    fallback:
      'This placement may violate IMDG segregation rules. If stacked in, a Yard Issue will be created with conflict details.',
  },
  [YardIssueType.ImdgStackingOnTopViolation]: {
    key: 'theSelectedPositionHasImdgStackingWarning',
    fallback:
      'This placement may violate DG stacking-on-top rules. If stacked in, a Yard Issue will be created with class details.',
  },
  [YardIssueType.ImdgTierCapViolation]: {
    key: 'theSelectedPositionHasImdgTierCapWarning',
    fallback:
      'This placement may exceed a DG tier cap. If stacked in, a Yard Issue will be created with class and tier details.',
  },
  [YardIssueType.AllocationRuleCapacityFull]: genericYardIssueWarning,
  [YardIssueType.BlockCapacityFull]: genericYardIssueWarning,
  [YardIssueType.YardCapacityFull]: genericYardIssueWarning,
  [YardIssueType.Unknown]: genericYardIssueWarning,
}

const blockPresentations: Partial<Record<YardIssueType, WarningPresentationCopy>> = {
  [YardIssueType.ImdgSegregationViolation]: {
    key: 'theSelectedPositionHasImdgSegregationBlock',
    fallback:
      'This placement violates IMDG segregation rules and is blocked. Choose another position or adjust DG rules.',
  },
  [YardIssueType.ImdgStackingOnTopViolation]: {
    key: 'theSelectedPositionHasImdgStackingBlock',
    fallback:
      'This placement violates DG stacking-on-top rules and is blocked. Choose another position or adjust DG rules.',
  },
  [YardIssueType.ImdgTierCapViolation]: {
    key: 'theSelectedPositionHasImdgTierCapBlock',
    fallback:
      'This placement exceeds a DG tier cap and is blocked. Choose another position or adjust DG rules.',
  },
}

const imdgStackInWarningTypes: ReadonlySet<YardIssueType> = new Set([
  YardIssueType.ImdgSegregationViolation,
  YardIssueType.ImdgStackingOnTopViolation,
  YardIssueType.ImdgTierCapViolation,
])

const severityByEnforcementMode: Record<EnforcementMode, StackInWarningPresentation['severity']> = {
  [EnforcementMode.Warn]: 'warning',
  [EnforcementMode.Block]: 'error',
}

const getPresentationCopy = (warning: StackInIssueCheckResponse): WarningPresentationCopy => {
  if (warning.enforcementMode === EnforcementMode.Block) {
    return blockPresentations[warning.issueType] ?? genericYardIssueBlock
  }

  return warningPresentations[warning.issueType] ?? genericYardIssueWarning
}

export const getStackInWarningPresentations = (
  warnings: StackInIssueCheckResponse[],
): StackInWarningPresentation[] =>
  warnings.map(warning => ({
    ...getPresentationCopy(warning),
    severity: severityByEnforcementMode[warning.enforcementMode],
  }))

export const hasImdgStackInWarning = (warnings: StackInIssueCheckResponse[]) =>
  warnings.some(warning => imdgStackInWarningTypes.has(warning.issueType))

export const getImdgStackInIssuesUrl = (containerNumber: string): string => {
  const searchParams = new URLSearchParams({ containerNumber })

  return `/issues?${searchParams.toString()}`
}

export const getImdgStackInWarningHandoffPresentation = (
  containerNumber?: string | null,
): ImdgStackInWarningHandoffPresentation => {
  const normalizedContainerNumber = containerNumber?.trim()

  if (!normalizedContainerNumber) {
    return {
      messageKey: 'imdgWarningCreated',
      messageFallback:
        'Stacked in with DG warning. A Yard Issue was created with details. Open the Issues page to review it.',
    }
  }

  return {
    messageKey: 'imdgWarningCreatedForContainer',
    messageFallback: `Stacked in ${normalizedContainerNumber} with DG warning. A Yard Issue was created with details.`,
    issuesUrl: getImdgStackInIssuesUrl(normalizedContainerNumber),
  }
}
